import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { SwalService } from '../../../../common/services/swal.service';
import { AuthService } from '../../../auth/services/auth.service';
import { TableComponent } from '../../../../common/components/table/table.component';
import { TableColumnDirective } from '../../../../common/directives/table-column.directive';
import { TableContainerComponent } from '../../../../common/components/table-container/table-container.component';
import { BankDirectorsService } from '../../services/bank-directors.service';
import { BankDirectorDto } from '../../../../core/data/new-dtos/bank-director.dtos';
import {
  DirectorInviteModalComponent,
  DirectorInviteModalResult,
} from '../director-invite-modal/director-invite-modal.component';
import {
  StatusChangeModalComponent,
  StatusChangeResult,
  StatusOption,
} from '../../../employees/ui/status-change-modal/status-change-modal.component';

@Component({
  selector: 'app-directors-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    TableColumnDirective,
    TableContainerComponent,
    DirectorInviteModalComponent,
    StatusChangeModalComponent,
  ],
  templateUrl: './directors-list.component.html',
})
export class DirectorsListComponent implements OnInit {
  directors: BankDirectorDto[] = [];
  roleFilter = '';
  statusFilter = '';

  showInviteModal = false;
  /** Set when the modal edits an existing regional's regions instead of inviting. */
  editingRegionsFor?: BankDirectorDto;

  statusChange: { id: number; current: string } | null = null;
  readonly statusOptions: StatusOption[] = [
    { value: 'Active', label: 'Activo' },
    { value: 'Inactive', label: 'Inactivo' },
  ];

  constructor(
    private swal: SwalService,
    private auth: AuthService,
    private directorsService: BankDirectorsService,
  ) { }

  get isSuperUser() {
    return this.auth.isSuperUser;
  }

  get tableHeaders() {
    const headers: any[] = [
      { attribute: 'name', label: 'Nombre' },
      { attribute: 'lastName', label: 'Apellido' },
      { attribute: 'email', label: 'Correo' },
    ];
    if (this.isSuperUser) {
      headers.push({ attribute: 'bankName', label: 'Banco' });
    }
    headers.push(
      { attribute: 'role', label: 'Rol' },
      { attribute: 'regions', label: 'Regiones' },
      { attribute: 'isLinked', label: 'Acceso' },
      { attribute: 'isActive', label: 'Estado' },
      { attribute: 'actions', label: 'Acciones' },
    );
    return headers;
  }

  async ngOnInit() {
    await this.auth.profileReady();
    await this.refresh();
  }

  async refresh() {
    this.swal.showLoading('Cargando administradores...');
    try {
      this.directors = await lastValueFrom(
        this.directorsService.getDirectors({
          role: this.roleFilter || undefined,
          status: this.statusFilter || undefined,
        }),
      );
    } catch (e: any) {
      this.directors = [];
      const message = e?.error?.error;
      if (message) {
        this.swal.error(message);
        return;
      }
    } finally {
      this.swal.dismiss();
    }
  }

  displayRole(role: string): string {
    return role === 'Global' ? 'Global' : 'Regional';
  }

  displayRegions(director: BankDirectorDto): string {
    if (director.role === 'Global') return 'Todo el banco';
    if (!director.regions.length) return 'Sin regiones';
    return director.regions.map((r) => r.name).join(', ');
  }

  openInviteModal() {
    this.editingRegionsFor = undefined;
    this.showInviteModal = true;
  }

  openRegionsModal(directorId: number) {
    const director = this.directors.find((d) => d.id === directorId);
    if (!director || director.role !== 'Regional') return;

    this.editingRegionsFor = director;
    this.showInviteModal = true;
  }

  async onInviteModalClosed(result: DirectorInviteModalResult) {
    const editing = this.editingRegionsFor;
    this.showInviteModal = false;
    this.editingRegionsFor = undefined;

    if (result.action !== 'save') return;

    this.swal.showLoading(editing ? 'Actualizando regiones...' : 'Enviando invitación...');
    try {
      if (editing) {
        await lastValueFrom(
          this.directorsService.replaceRegions(editing.id, result.regionIds ?? []),
        );
        await this.swal.success('Regiones actualizadas.');
      } else if (result.invite) {
        await lastValueFrom(this.directorsService.invite(result.invite));
        await this.swal.success(
          'Invitación creada. El usuario obtendrá acceso al iniciar sesión con este correo.',
        );
      }
      await this.refresh();
    } catch (e: any) {
      this.swal.error(e?.error?.error ?? 'No se pudo guardar.');
    }
  }

  openStatusModal(directorId: number) {
    const director = this.directors.find((d) => d.id === directorId);
    if (!director) return;

    this.statusChange = { id: directorId, current: director.isActive ? 'Active' : 'Inactive' };
  }

  async onStatusModalClosed(result: StatusChangeResult) {
    const target = this.statusChange;
    this.statusChange = null;
    if (!target || result.action !== 'confirm' || !result.status) return;

    this.swal.showLoading('Actualizando estado...');
    try {
      await lastValueFrom(
        this.directorsService.changeStatus(target.id, result.status === 'Active'),
      );
      await this.swal.success('Estado actualizado.');
      await this.refresh();
    } catch (e: any) {
      this.swal.error(e?.error?.error ?? 'No se pudo actualizar el estado.');
    }
  }

  async resendInvite(directorId: number) {
    this.swal.showLoading('Reenviando invitación...');
    try {
      await lastValueFrom(this.directorsService.resendInvite(directorId));
      await this.swal.success('Invitación reenviada.');
    } catch (e: any) {
      this.swal.error(e?.error?.error ?? 'No se pudo reenviar la invitación.');
    }
  }

  async deleteInvite(directorId: number) {
    const { isConfirmed } = await this.swal.confirm(
      'La invitación se eliminará y el correo quedará libre.',
      '¿Eliminar invitación?',
      { confirmButtonText: 'Sí, eliminar' },
    );

    if (!isConfirmed) return;

    this.swal.showLoading('Eliminando invitación...');
    try {
      await lastValueFrom(this.directorsService.deleteInvite(directorId));
      await this.swal.success('Invitación eliminada.');
      await this.refresh();
    } catch (e: any) {
      this.swal.error(e?.error?.error ?? 'No se pudo eliminar la invitación.');
    }
  }
}
